import { logger } from '../utils/logger';
import { cookieService } from './cookieService';

const DEFAULT_TRACKING_ID = 'G-TVCG63M3Z1';

const PUBLIC_ACQUISITION_PATHS = new Set([
  '/',
  '/about',
  '/contact',
  '/cookie-policy',
  '/faq',
  '/how-it-works',
  '/pricing',
  '/privacy-policy',
  '/register',
  '/requests',
  '/terms-of-service'
]);

let analyticsModulePromise = null;
let initializedTrackingId = null;

const loadAnalytics = () => {
  if (!analyticsModulePromise) {
    analyticsModulePromise = import('react-ga4')
      .then((module) => module.default || module)
      .catch((error) => {
        analyticsModulePromise = null;
        logger.warn('Google Analytics failed to load', { error });
        return null;
      });
  }

  return analyticsModulePromise;
};

export const getTrackingId = () => (
  import.meta.env.VITE_GA_TRACKING_ID || DEFAULT_TRACKING_ID
);

export const isPublicAcquisitionPath = (path = '') => {
  const pathname = path.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';
  return PUBLIC_ACQUISITION_PATHS.has(pathname);
};

export const initializeAnalytics = async (trackingId = getTrackingId()) => {
  if (!trackingId) return null;
  if (initializedTrackingId === trackingId) return true;

  const analytics = await loadAnalytics();
  if (!analytics) return null;

  analytics.initialize(trackingId);
  initializedTrackingId = trackingId;
  return true;
};

export const trackPageView = async (path, {
  trackingId = getTrackingId(),
  acquisitionOnly = false
} = {}) => {
  if (!path) return false;
  if (acquisitionOnly && !isPublicAcquisitionPath(path)) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (!cookieService.hasConsent() || cookieService.getPreferences()?.analytics !== true) return false;

  const initialized = await initializeAnalytics(trackingId);
  if (!initialized) return false;

  const analytics = await loadAnalytics();
  if (!analytics) return false;

  analytics.send({ hitType: 'pageview', page: path });
  return true;
};

export const trackEvent = async (name, parameters = {}, { trackingId = getTrackingId() } = {}) => {
  if (!name) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (!cookieService.hasConsent() || cookieService.getPreferences()?.analytics !== true) return false;

  const initialized = await initializeAnalytics(trackingId);
  if (!initialized) return false;

  const analytics = await loadAnalytics();
  if (!analytics) return false;

  analytics.event(name, parameters);
  return true;
};
